import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Building2, Phone, Mail, Globe, MapPin, DollarSign, Star,
  Plus, Search, Loader2, Edit2, Trash2, Eye, MessageSquare, Calendar,
  TrendingUp, Target, Clock, CheckCircle, XCircle, ArrowRight, User,
  Activity, Filter, RefreshCw
} from 'lucide-react';

const LEAD_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700', icon: '🆕' },
  { value: 'contacted', label: 'Contacted', color: 'bg-purple-100 text-purple-700', icon: '📞' },
  { value: 'qualified', label: 'Qualified', color: 'bg-cyan-100 text-cyan-700', icon: '✅' },
  { value: 'proposal', label: 'Proposal', color: 'bg-amber-100 text-amber-700', icon: '📄' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-orange-100 text-orange-700', icon: '🤝' },
  { value: 'won', label: 'Won', color: 'bg-emerald-100 text-emerald-700', icon: '🎉' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700', icon: '❌' }
];

const LEAD_SOURCES = [
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'conference', label: 'Conference' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'email_campaign', label: 'Email Campaign' },
  { value: 'partner', label: 'Partner' },
  { value: 'other', label: 'Other' }
];

const INTEREST_LEVELS = [
  { value: 'low', label: 'Low', color: 'text-slate-500' },
  { value: 'medium', label: 'Medium', color: 'text-amber-500' },
  { value: 'high', label: 'High', color: 'text-orange-500' },
  { value: 'very_high', label: 'Very High', color: 'text-red-500' }
];

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Phone Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'meeting', label: 'Meeting', icon: Users },
  { value: 'note', label: 'Note', icon: MessageSquare },
  { value: 'follow_up', label: 'Follow-up', icon: Calendar }
];

const CRMSection = () => {
  const { api } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [pipelineStats, setPipelineStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    organization_name: '',
    contact_name: '',
    contact_email: '',
    contact_title: '',
    phone: '',
    website: '',
    address: '',
    estimated_students: 0,
    estimated_value: 0,
    source: 'website',
    status: 'new',
    interest_level: 'medium',
    organization_type: 'school',
    notes: '',
    follow_up_date: ''
  });
  
  const [activityData, setActivityData] = useState({
    type: 'note',
    description: '',
    notes: '',
    outcome: '',
    follow_up_date: ''
  });

  useEffect(() => {
    fetchLeads();
    fetchPipelineStats();
  }, [statusFilter, sourceFilter]);

  const fetchLeads = async () => {
    try {
      let url = '/admin/leads';
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (sourceFilter) params.append('source', sourceFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += '?' + params.toString();
      
      const response = await api.get(url);
      setLeads(response.data.leads || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchPipelineStats = async () => {
    try {
      const response = await api.get('/admin/leads/pipeline/stats');
      setPipelineStats(response.data);
    } catch (error) {
      console.error('Failed to fetch pipeline stats:', error);
    }
  };

  const handleAddLead = async () => {
    setSubmitting(true);
    try {
      await api.post('/admin/leads', formData);
      toast.success('Lead created successfully');
      setShowAddDialog(false);
      resetForm();
      fetchLeads();
      fetchPipelineStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateLead = async () => {
    if (!selectedLead) return;
    setSubmitting(true);
    try {
      await api.put(`/admin/leads/${selectedLead.id}`, formData);
      toast.success('Lead updated successfully');
      setShowEditDialog(false);
      fetchLeads();
      fetchPipelineStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await api.delete(`/admin/leads/${leadId}`);
      toast.success('Lead deleted');
      fetchLeads();
      fetchPipelineStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const handleAddActivity = async () => {
    if (!selectedLead) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/leads/${selectedLead.id}/activities`, activityData);
      toast.success('Activity added');
      setShowActivityDialog(false);
      setActivityData({ type: 'note', description: '', notes: '', outcome: '', follow_up_date: '' });
      // Refresh lead details
      const response = await api.get(`/admin/leads/${selectedLead.id}`);
      setSelectedLead(response.data);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add activity');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.put(`/admin/leads/${leadId}`, { status: newStatus });
      toast.success('Status updated');
      fetchLeads();
      fetchPipelineStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      organization_name: '',
      contact_name: '',
      contact_email: '',
      contact_title: '',
      phone: '',
      website: '',
      address: '',
      estimated_students: 0,
      estimated_value: 0,
      source: 'website',
      status: 'new',
      interest_level: 'medium',
      organization_type: 'school',
      notes: '',
      follow_up_date: ''
    });
  };

  const openEditDialog = (lead) => {
    setSelectedLead(lead);
    setFormData({
      organization_name: lead.organization_name || '',
      contact_name: lead.contact_name || '',
      contact_email: lead.contact_email || '',
      contact_title: lead.contact_title || '',
      phone: lead.phone || '',
      website: lead.website || '',
      address: lead.address || '',
      estimated_students: lead.estimated_students || 0,
      estimated_value: lead.estimated_value || 0,
      source: lead.source || 'website',
      status: lead.status || 'new',
      interest_level: lead.interest_level || 'medium',
      organization_type: lead.organization_type || 'school',
      notes: lead.notes || '',
      follow_up_date: lead.follow_up_date || ''
    });
    setShowEditDialog(true);
  };

  const openViewDialog = async (lead) => {
    try {
      const response = await api.get(`/admin/leads/${lead.id}`);
      setSelectedLead(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load lead details');
    }
  };

  const getStatusInfo = (status) => {
    return LEAD_STATUSES.find(s => s.value === status) || LEAD_STATUSES[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-section">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">CRM / Lead Management</h1>
        <Button onClick={() => { resetForm(); setShowAddDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Lead
        </Button>
      </div>

      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {LEAD_STATUSES.map((status) => {
          const statusData = pipelineStats.by_status?.[status.value] || { count: 0, total_value: 0 };
          return (
            <Card 
              key={status.value} 
              className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === status.value ? 'ring-2 ring-emerald-500' : ''}`}
              onClick={() => setStatusFilter(statusFilter === status.value ? '' : status.value)}
            >
              <CardContent className="p-3 text-center">
                <span className="text-2xl">{status.icon}</span>
                <p className="font-bold text-lg">{statusData.count}</p>
                <p className="text-xs text-slate-500">{status.label}</p>
                <p className="text-xs text-emerald-600">${statusData.total_value?.toLocaleString() || 0}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total || 0}</p>
                <p className="text-sm text-slate-500">Total Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">${stats.total_value?.toLocaleString() || 0}</p>
                <p className="text-sm text-slate-500">Pipeline Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pipelineStats.conversion_rate || 0}%</p>
                <p className="text-sm text-slate-500">Conversion Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pipelineStats.overdue_followups || 0}</p>
                <p className="text-sm text-slate-500">Overdue Follow-ups</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchLeads()}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={sourceFilter || 'all'} onValueChange={(v) => setSourceFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {LEAD_SOURCES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { setStatusFilter(''); setSourceFilter(''); setSearchQuery(''); fetchLeads(); }}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Follow-up</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No leads found. Click "Add Lead" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => {
                  const statusInfo = getStatusInfo(lead.status);
                  const interestInfo = INTEREST_LEVELS.find(l => l.value === lead.interest_level);
                  return (
                    <TableRow key={lead.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{lead.organization_name}</p>
                          <p className="text-xs text-slate-500">{lead.organization_type}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{lead.contact_name}</p>
                          <p className="text-xs text-slate-500">{lead.contact_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select value={lead.status} onValueChange={(v) => handleStatusChange(lead.id, v)}>
                          <SelectTrigger className={`w-[130px] ${statusInfo.color}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEAD_STATUSES.map(s => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.icon} {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-bold">${lead.estimated_value?.toLocaleString() || 0}</p>
                          <p className="text-xs text-slate-500">{lead.estimated_students} students</p>
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">{lead.source?.replace('_', ' ')}</TableCell>
                      <TableCell>
                        {lead.follow_up_date ? (
                          <span className={`text-sm ${new Date(lead.follow_up_date) < new Date() ? 'text-red-600' : 'text-slate-600'}`}>
                            {new Date(lead.follow_up_date).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-slate-400">Not set</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openViewDialog(lead)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(lead)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteLead(lead.id)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Lead Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => { if (!open) { setShowAddDialog(false); setShowEditDialog(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
            <DialogDescription>
              {showEditDialog ? 'Update lead information' : 'Enter the lead details below'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Organization Name *</Label>
              <Input value={formData.organization_name} onChange={(e) => setFormData({...formData, organization_name: e.target.value})} />
            </div>
            <div>
              <Label>Contact Name *</Label>
              <Input value={formData.contact_name} onChange={(e) => setFormData({...formData, contact_name: e.target.value})} />
            </div>
            <div>
              <Label>Contact Email *</Label>
              <Input type="email" value={formData.contact_email} onChange={(e) => setFormData({...formData, contact_email: e.target.value})} />
            </div>
            <div>
              <Label>Contact Title</Label>
              <Input value={formData.contact_title} onChange={(e) => setFormData({...formData, contact_title: e.target.value})} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
            </div>
            <div>
              <Label>Website</Label>
              <Input value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} />
            </div>
            <div>
              <Label>Organization Type</Label>
              <Select value={formData.organization_type} onValueChange={(v) => setFormData({...formData, organization_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="school">School</SelectItem>
                  <SelectItem value="district">District</SelectItem>
                  <SelectItem value="homeschool">Homeschool</SelectItem>
                  <SelectItem value="church">Church</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estimated Students</Label>
              <Input type="number" value={formData.estimated_students} onChange={(e) => setFormData({...formData, estimated_students: parseInt(e.target.value) || 0})} />
            </div>
            <div>
              <Label>Estimated Value ($)</Label>
              <Input type="number" value={formData.estimated_value} onChange={(e) => setFormData({...formData, estimated_value: parseFloat(e.target.value) || 0})} />
            </div>
            <div>
              <Label>Source</Label>
              <Select value={formData.source} onValueChange={(v) => setFormData({...formData, source: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Interest Level</Label>
              <Select value={formData.interest_level} onValueChange={(v) => setFormData({...formData, interest_level: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INTEREST_LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.icon} {s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Follow-up Date</Label>
              <Input type="date" value={formData.follow_up_date} onChange={(e) => setFormData({...formData, follow_up_date: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); setShowEditDialog(false); }}>Cancel</Button>
            <Button onClick={showEditDialog ? handleUpdateLead : handleAddLead} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {showEditDialog ? 'Update Lead' : 'Add Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Lead Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {selectedLead?.organization_name}
            </DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-6">
              {/* Lead Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Contact</p>
                  <p className="font-medium">{selectedLead.contact_name}</p>
                  <p className="text-sm text-slate-600">{selectedLead.contact_title}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedLead.contact_email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium">{selectedLead.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Value</p>
                  <p className="font-bold text-emerald-600">${selectedLead.estimated_value?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Students</p>
                  <p className="font-medium">{selectedLead.estimated_students}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusInfo(selectedLead.status).color}`}>
                    {getStatusInfo(selectedLead.status).icon} {getStatusInfo(selectedLead.status).label}
                  </span>
                </div>
              </div>

              {/* Activities */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">Activity Log</h3>
                  <Button size="sm" onClick={() => setShowActivityDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Activity
                  </Button>
                </div>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {selectedLead.activities?.length > 0 ? (
                    selectedLead.activities.slice().reverse().map((activity) => {
                      const actType = ACTIVITY_TYPES.find(t => t.value === activity.type);
                      const IconComponent = actType?.icon || MessageSquare;
                      return (
                        <div key={activity.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm">
                            <IconComponent className="w-4 h-4 text-slate-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{activity.description}</p>
                            {activity.notes && <p className="text-sm text-slate-600 mt-1">{activity.notes}</p>}
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(activity.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-slate-500 text-center py-4">No activities yet</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Activity Dialog */}
      <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Activity</DialogTitle>
            <DialogDescription>Log an interaction with this lead</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Activity Type</Label>
              <Select value={activityData.type} onValueChange={(v) => setActivityData({...activityData, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Input value={activityData.description} onChange={(e) => setActivityData({...activityData, description: e.target.value})} placeholder="Brief description of the activity" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={activityData.notes} onChange={(e) => setActivityData({...activityData, notes: e.target.value})} rows={3} placeholder="Detailed notes..." />
            </div>
            <div>
              <Label>Outcome</Label>
              <Select value={activityData.outcome} onValueChange={(v) => setActivityData({...activityData, outcome: v})}>
                <SelectTrigger><SelectValue placeholder="Select outcome" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="positive">Positive</SelectItem>
                  <SelectItem value="neutral">Neutral</SelectItem>
                  <SelectItem value="negative">Negative</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Schedule Follow-up</Label>
              <Input type="date" value={activityData.follow_up_date} onChange={(e) => setActivityData({...activityData, follow_up_date: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivityDialog(false)}>Cancel</Button>
            <Button onClick={handleAddActivity} disabled={submitting || !activityData.description} className="bg-emerald-600 hover:bg-emerald-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Activity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMSection;
